import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Text,
  useColorModeValue,
  Icon,
  useToast,
  Grid,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Heading,
  FormHelperText,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdSettings } from 'react-icons/md';
import { 
  getGlobalPayrollRates,
  updateGlobalPayrollRates
} from 'services/payroll-extended.service';
import {
  getActiveIrpfConfig,
  createIrpfConfig,
  deleteIrpfConfig,
  getAllIrpfConfigs,
} from 'services/irpf.service';

export default function EmployeePayrollConfigList() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const irpfTableHeadBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const toast = useToast();
  
  // Valores globales de aportes
  const [globalRates, setGlobalRates] = useState({
    bpsRate: 15.0,
    frlRate: 0.10,
    fonasaBase: 4.5,
    fonasaWithSpouse: 6.0,
    fonasaWithSpouseAndChildren: 8.0,
    fonasaOneChild: 5.0,
    fonasaTwoChildren: 6.0,
    fonasaThreeOrMoreChildren: 8.0,
  });
  const [editingGlobal, setEditingGlobal] = useState(false);

  // ==================== IRPF (franjas) ====================
  const today = new Date();
  const [irpfYear, setIrpfYear] = useState(today.getFullYear());
  const [irpfMonth, setIrpfMonth] = useState(today.getMonth() + 1);
  const [irpfLoading, setIrpfLoading] = useState(false);
  const [irpfConfigId, setIrpfConfigId] = useState(null);
  const [irpfBpcValue, setIrpfBpcValue] = useState(0);
  const [irpfDeductionRate, setIrpfDeductionRate] = useState(0);
  const [irpfEffectiveDate, setIrpfEffectiveDate] = useState(today.toISOString().slice(0, 10));
  const [irpfDescription, setIrpfDescription] = useState('');
  const [irpfCommunicationNumber, setIrpfCommunicationNumber] = useState('');
  const [irpfBrackets, setIrpfBrackets] = useState([
    { bpcFrom: '', bpcTo: '', rate: '', description: '' },
  ]);

  useEffect(() => {
    loadGlobalRates();
  }, []);

  const handleSaveGlobalRates = async () => {
    try {
      const response = await updateGlobalPayrollRates({
        bpsRate: globalRates.bpsRate / 100,
        frlRate: globalRates.frlRate / 100,
        fonasaBaseRate: globalRates.fonasaBase / 100,
        fonasaWithSpouseRate: globalRates.fonasaWithSpouse / 100,
        fonasaOneChildRate: globalRates.fonasaOneChild / 100,
        fonasaTwoChildrenRate: globalRates.fonasaTwoChildren / 100,
        fonasaThreeOrMoreChildrenRate: globalRates.fonasaThreeOrMoreChildren / 100,
        fonasaWithSpouseAndChildrenRate: globalRates.fonasaWithSpouseAndChildren / 100,
      });

      if (response.status === 200) {
        setEditingGlobal(false);
        toast({
          title: 'Éxito',
          description: 'Tasas globales actualizadas correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error guardando tasas globales:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron actualizar las tasas globales',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancelGlobalEdit = () => {
    loadGlobalRates();
    setEditingGlobal(false);
  };

  const loadGlobalRates = async () => {
    try {
      const response = await getGlobalPayrollRates();
      if (response.status === 200 && response.payload) {
        const rates = response.payload;
        setGlobalRates({
          bpsRate: (rates.bps_rate * 100).toFixed(2),
          frlRate: (rates.frl_rate * 100).toFixed(2),
          fonasaBase: (rates.fonasa_base_rate * 100).toFixed(2),
          fonasaWithSpouse: (rates.fonasa_with_spouse_rate * 100).toFixed(2),
          fonasaOneChild: (rates.fonasa_one_child_rate * 100).toFixed(2),
          fonasaTwoChildren: (rates.fonasa_two_children_rate * 100).toFixed(2),
          fonasaThreeOrMoreChildren: (rates.fonasa_three_or_more_children_rate * 100).toFixed(2),
          fonasaWithSpouseAndChildren: (rates.fonasa_with_spouse_and_children_rate * 100).toFixed(2),
        });
      }
    } catch (error) {
      console.error('Error cargando tasas globales:', error);
    }
  };

  useEffect(() => {
    loadGlobalRates();
  }, []);

  const loadIrpfActiveConfig = async (year, month, opts = { silent: false }) => {
    const y = Number(year);
    const m = Number(month);
    setIrpfLoading(true);
    try {
      const response = await getActiveIrpfConfig(y, m);
      if (response?.status === 200 && response?.payload?.config) {
        const cfg = response.payload.config;
        setIrpfConfigId(cfg.id != null ? Number(cfg.id) : null);
        setIrpfBpcValue(Number(cfg.bpc_value) || 0);
        setIrpfDeductionRate(Number(cfg.deduction_rate) || 0);
        setIrpfDescription(cfg.description ?? '');
        setIrpfCommunicationNumber(cfg.communication_number ?? '');
        setIrpfEffectiveDate(
          typeof cfg.effective_date === 'string' && cfg.effective_date
            ? cfg.effective_date.slice(0, 10)
            : today.toISOString().slice(0, 10),
        );

        const loadedBrackets = Array.isArray(cfg.brackets) ? cfg.brackets : [];
        setIrpfBrackets(
          loadedBrackets.length > 0
            ? loadedBrackets.map((b) => ({
                bpcFrom: String(b.bpc_from ?? ''),
                bpcTo: b.bpc_to == null ? '' : String(b.bpc_to),
                rate: String(b.rate ?? ''),
                description: b.description ?? '',
              }))
            : [{ bpcFrom: '', bpcTo: '', rate: '', description: '' }],
        );
      }
    } catch (error) {
      // Si no hay config activa, dejamos el formulario vacío para poder crearla.
      setIrpfConfigId(null);
      setIrpfBpcValue(0);
      setIrpfDeductionRate(0);
      setIrpfDescription('');
      setIrpfCommunicationNumber('');
      setIrpfBrackets([{ bpcFrom: '', bpcTo: '', rate: '', description: '' }]);
      if (!opts.silent) {
        toast({
          title: 'IRPF',
          description: 'No hay configuración activa para este año/mes. Podés crear una nueva.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setIrpfLoading(false);
    }
  };

  const loadLatestActiveIrpfConfig = async () => {
    setIrpfLoading(true);
    try {
      const res = await getAllIrpfConfigs({ isActive: true });
      const list = res?.payload?.configs ?? [];
      if (!Array.isArray(list) || list.length === 0) return false;

      const latest = list[0];
      const y = Number(latest.year);
      const m = Number(latest.month);
      if (!Number.isFinite(y) || !Number.isFinite(m)) return false;

      setIrpfYear(y);
      setIrpfMonth(m);
      await loadIrpfActiveConfig(y, m, { silent: true });
      return true;
    } catch {
      return false;
    } finally {
      setIrpfLoading(false);
    }
  };

  useEffect(() => {
    // Pre-carga:
    // 1) Intentar con el año/mes “actual”
    // 2) Si no existe config activa, cargar la última config activa disponible.
    (async () => {
      await loadIrpfActiveConfig(irpfYear, irpfMonth, { silent: true });
      // Si quedaron valores vacíos y no hay config activa, intentar con la última activa.
      if (!irpfConfigId) {
        await loadLatestActiveIrpfConfig();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateBracketField = (idx, field, value) => {
    setIrpfBrackets((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleSaveIrpfConfig = async () => {
    const bpcValueNum = Number(irpfBpcValue);
    const deductionRateNum = Number(irpfDeductionRate);

    if (!Number.isFinite(bpcValueNum) || bpcValueNum <= 0) {
      toast({ title: 'IRPF', description: 'El valor de BPC debe ser mayor a 0.', status: 'error', duration: 3000 });
      return;
    }
    if (!Number.isFinite(deductionRateNum) || deductionRateNum < 0) {
      toast({
        title: 'IRPF',
        description: 'La tasa de deducción debe ser un número válido (ej: 0.08).',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const cleanedBrackets = irpfBrackets
      .map((br) => {
        const bpcFromStr = String(br.bpcFrom ?? '');
        const rateStr = String(br.rate ?? '');
        const bpcToStr = br.bpcTo == null ? '' : String(br.bpcTo);
        return {
          bpcFrom: bpcFromStr === '' ? NaN : Number(bpcFromStr),
          bpcTo: bpcToStr === '' ? null : Number(bpcToStr),
          rate: rateStr === '' ? NaN : Number(rateStr),
          description: br.description ?? '',
        };
      })
      .filter(
        (br) =>
          Number.isFinite(br.bpcFrom) &&
          br.bpcFrom >= 0 &&
          Number.isFinite(br.rate) &&
          br.rate >= 0,
      );

    if (cleanedBrackets.length === 0) {
      toast({ title: 'IRPF', description: 'Evitá guardar sin franjas válidas.', status: 'error', duration: 3000 });
      return;
    }

    const payload = {
      year: Number(irpfYear),
      month: Number(irpfMonth),
      bpcValue: bpcValueNum,
      deductionRate: deductionRateNum,
      description: irpfDescription || undefined,
      communicationNumber: irpfCommunicationNumber || undefined,
      effectiveDate: irpfEffectiveDate,
      brackets: cleanedBrackets.map((br, i) => ({
        bracketOrder: i + 1,
        bpcFrom: br.bpcFrom,
        bpcTo: br.bpcTo == null ? undefined : br.bpcTo,
        amountFrom: br.bpcFrom * bpcValueNum,
        amountTo: br.bpcTo == null ? undefined : br.bpcTo * bpcValueNum,
        rate: br.rate,
        description: br.description || undefined,
      })),
    };

    try {
      if (irpfConfigId != null) {
        await deleteIrpfConfig(irpfConfigId);
      }
      await createIrpfConfig(payload);
      toast({
        title: 'Éxito',
        description: 'Configuración IRPF creada/actualizada correctamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await loadIrpfActiveConfig(irpfYear, irpfMonth);
    } catch (error) {
      console.error('Error guardando IRPF:', error);
      toast({
        title: 'Error',
        description: error?.message || 'No se pudo guardar la configuración IRPF.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Card de Configuración Global */}
      <Card direction="column" w="100%" px="25px" mb="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex align="center">
            <Icon as={MdSettings} w={6} h={6} color="brand.500" mr={3} />
            <Heading size="md" color={textColor}>
              Configuración Global de Aportes
            </Heading>
          </Flex>
          {!editingGlobal ? (
            <Button colorScheme="brand" size="sm" onClick={() => setEditingGlobal(true)}>
              Editar Tasas
            </Button>
          ) : (
            <Flex gap={2}>
              <Button colorScheme="brand" size="sm" onClick={handleSaveGlobalRates}>
                Guardar
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancelGlobalEdit}>
                Cancelar
              </Button>
            </Flex>
          )}
        </Flex>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
          <FormControl>
            <FormLabel fontSize="sm">BPS (%)</FormLabel>
            <NumberInput
              value={globalRates.bpsRate}
              onChange={(val) => setGlobalRates({...globalRates, bpsRate: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={100}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">Aplicable a todos</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">FRL (%)</FormLabel>
            <NumberInput
              value={globalRates.frlRate}
              onChange={(val) => setGlobalRates({...globalRates, frlRate: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={5}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">Aplicable a todos</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">FONASA Base (%)</FormLabel>
            <NumberInput
              value={globalRates.fonasaBase}
              onChange={(val) => setGlobalRates({...globalRates, fonasaBase: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={20}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">Sin cargas</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">FONASA + Cónyuge (%)</FormLabel>
            <NumberInput
              value={globalRates.fonasaWithSpouse}
              onChange={(val) => setGlobalRates({...globalRates, fonasaWithSpouse: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={20}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">Con cónyuge sin hijos</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">FONASA 1 Hijo (%)</FormLabel>
            <NumberInput
              value={globalRates.fonasaOneChild}
              onChange={(val) => setGlobalRates({...globalRates, fonasaOneChild: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={20}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">1 hijo a cargo</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">FONASA 2 Hijos (%)</FormLabel>
            <NumberInput
              value={globalRates.fonasaTwoChildren}
              onChange={(val) => setGlobalRates({...globalRates, fonasaTwoChildren: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={20}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">2 hijos a cargo</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">FONASA 3+ Hijos (%)</FormLabel>
            <NumberInput
              value={globalRates.fonasaThreeOrMoreChildren}
              onChange={(val) => setGlobalRates({...globalRates, fonasaThreeOrMoreChildren: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={20}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">3 o más hijos</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">FONASA Cónyuge + Hijos (%)</FormLabel>
            <NumberInput
              value={globalRates.fonasaWithSpouseAndChildren}
              onChange={(val) => setGlobalRates({...globalRates, fonasaWithSpouseAndChildren: parseFloat(val) || 0})}
              isDisabled={!editingGlobal}
              min={0}
              max={20}
              precision={2}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">Cónyuge + hijos</FormHelperText>
          </FormControl>
        </Grid>
      </Card>

      <Divider mb="20px" />

      {/* Card: Configuración IRPF */}
      <Card direction="column" w="100%" px="25px" mb="20px">
        <Flex justify="space-between" align="center" mb="20px" flexWrap="wrap" gap={3}>
          <Flex align="center">
            <Icon as={MdSettings} w={6} h={6} color="brand.500" mr={3} />
            <Heading size="md" color={textColor}>
              Configuración IRPF (franjas)
            </Heading>
          </Flex>
          <Flex gap={2} align="center">
            <FormControl w="140px">
              <FormLabel fontSize="sm">Año</FormLabel>
              <NumberInput
                value={irpfYear}
                onChange={(val) => setIrpfYear(parseInt(val, 10) || 0)}
                min={2000}
                max={2100}
                precision={0}
                disabled={irpfLoading}
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl w="110px">
              <FormLabel fontSize="sm">Mes</FormLabel>
              <NumberInput
                value={irpfMonth}
                onChange={(val) => setIrpfMonth(parseInt(val, 10) || 0)}
                min={1}
                max={12}
                precision={0}
                disabled={irpfLoading}
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <Button
              colorScheme="brand"
              size="sm"
              onClick={() => loadIrpfActiveConfig(irpfYear, irpfMonth)}
            >
              Cargar activa
            </Button>
          </Flex>
        </Flex>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4} mb={4}>
          <FormControl>
            <FormLabel fontSize="sm">BPC (valor)</FormLabel>
            <NumberInput
              value={irpfBpcValue}
              onChange={(val) => setIrpfBpcValue(parseFloat(val) || 0)}
              min={0}
              precision={2}
              step={0.01}
              disabled={irpfLoading}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">Usado para convertir ingreso a BPC.</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Deducción (tasa)</FormLabel>
            <NumberInput
              value={irpfDeductionRate}
              onChange={(val) => setIrpfDeductionRate(parseFloat(val) || 0)}
              min={0}
              precision={4}
              step={0.0001}
              disabled={irpfLoading}
            >
              <NumberInputField />
            </NumberInput>
            <FormHelperText fontSize="xs">Ejemplo: `0.08` (8%).</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Fecha efectiva</FormLabel>
            <Input
              type="date"
              value={irpfEffectiveDate}
              onChange={(e) => setIrpfEffectiveDate(e.target.value)}
              disabled={irpfLoading}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Nro. comunicación (opcional)</FormLabel>
            <Input
              value={irpfCommunicationNumber}
              onChange={(e) => setIrpfCommunicationNumber(e.target.value)}
              placeholder="Ej: Ley/Comunicación..."
              disabled={irpfLoading}
            />
          </FormControl>
        </Grid>

        <FormControl mb={4}>
          <FormLabel fontSize="sm">Descripción (opcional)</FormLabel>
          <Input
            value={irpfDescription}
            onChange={(e) => setIrpfDescription(e.target.value)}
            placeholder="Ej: Tramos vigentes 2026..."
            disabled={irpfLoading}
          />
        </FormControl>

        <Heading size="xs" mt={4} mb={2} textTransform="uppercase" color="gray.500">
          Franjas (BPC anual)
        </Heading>

        <Table size="sm" variant="simple" mb={4}>
          <Thead bg={irpfTableHeadBg}>
            <Tr>
              <Th textAlign="center">N°</Th>
              <Th isNumeric textAlign="right">BPC desde</Th>
              <Th isNumeric textAlign="right">BPC hasta</Th>
              <Th isNumeric textAlign="right">Tasa (%)</Th>
              <Th>Descripción</Th>
              <Th>Acción</Th>
            </Tr>
          </Thead>
          <Tbody>
            {irpfBrackets.map((br, idx) => (
              <Tr key={idx}>
                <Td textAlign="center">{idx + 1}</Td>
                <Td isNumeric textAlign="right">
                  <Input
                    size="sm"
                    type="number"
                    value={br.bpcFrom}
                    onChange={(e) => updateBracketField(idx, 'bpcFrom', e.target.value)}
                    disabled={irpfLoading}
                    textAlign="right"
                  />
                </Td>
                <Td isNumeric textAlign="right">
                  <Input
                    size="sm"
                    type="number"
                    value={br.bpcTo}
                    onChange={(e) => updateBracketField(idx, 'bpcTo', e.target.value)}
                    placeholder="(última: vacío)"
                    disabled={irpfLoading}
                    textAlign="right"
                  />
                </Td>
                <Td isNumeric textAlign="right">
                  <Input
                    size="sm"
                    type="number"
                    value={br.rate}
                    onChange={(e) => updateBracketField(idx, 'rate', e.target.value)}
                    disabled={irpfLoading}
                    textAlign="right"
                  />
                </Td>
                <Td>
                  <Input
                    size="sm"
                    value={br.description}
                    onChange={(e) => updateBracketField(idx, 'description', e.target.value)}
                    disabled={irpfLoading}
                  />
                </Td>
                <Td>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="red"
                    onClick={() =>
                      setIrpfBrackets((prev) => prev.filter((_, i) => i !== idx))
                    }
                    disabled={irpfLoading || irpfBrackets.length <= 1}
                  >
                    Quitar
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Flex gap={3} mb={4} flexWrap="wrap">
          <Button
            size="sm"
            variant="outline"
            colorScheme="brand"
            onClick={() =>
              setIrpfBrackets((prev) => [
                ...prev,
                { bpcFrom: '', bpcTo: '', rate: '', description: '' },
              ])
            }
            disabled={irpfLoading}
          >
            Agregar franja
          </Button>

          <Text fontSize="xs" color="gray.500">
            Para la última franja dejá `BPC hasta` vacío. La aplicación calcula `monto desde/hasta` automáticamente.
          </Text>
        </Flex>

        <Flex justify="flex-end">
          <Button colorScheme="brand" onClick={handleSaveIrpfConfig} minW="220px">
            Guardar franjas IRPF
          </Button>
        </Flex>
      </Card>
    </Box>
  );
}
